import { useState } from 'react'
import { Box, Button, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { useHasEnablement } from '@portal/pkg-authorization'
import { useExportEntitlementsCsv } from '../mutations'
import { RoleForm } from './RoleForm'
import { RolesTable } from './RolesTable'

/**
 * The add drawer is held here rather than reached through a route, for the same reason the row
 * menu's edit drawer is: the role detail page is a child of this page's route, so a route that
 * rendered a drawer over the grid would put the grid above the detail page too.
 */
export function RolesPage() {
  const { t } = useTranslation('admin-console')
  const canManage = useHasEnablement('Role', 'MANAGE', { global: true })
  const exportCsv = useExportEntitlementsCsv()
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)

  return (
    <Stack
      spacing={2}
      // The app shell clips its content, so each page scrolls itself.
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 2,
        pt: 2,
        pb: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('roles.page.title')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          placeholder={t('roles.page.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={exportCsv.isPending}
            onClick={() => exportCsv.mutate()}
            sx={{
              backgroundColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { backgroundColor: 'grey.400' },
            }}
          >
            {t('roles.page.exportCsv')}
          </Button>
          {canManage && (
            <Button variant="contained" size="small" onClick={() => setAdding(true)}>
              {t('roles.page.addRole')}
            </Button>
          )}
        </Stack>
      </Box>

      <RolesTable search={search} />
      {adding && <RoleForm onClose={() => setAdding(false)} />}
    </Stack>
  )
}
